// KyroLabs — 38 unique AI agents, grouped by FUNCTION (not industry).
// Merged: Supply Chain (was ×2), Dealer Support (was ×2), Field Service
// (Field Technician / Field Services / Field Service = 1 agent).
//
// Each entry is used both by the /use-cases page and by /use-cases/:id detail.
// `p` and `s` power the Problem / Solution blocks on the detail page.
// Outcome tags have been removed everywhere per the latest brief.

// Functional groups drive the Use Cases mega-menu AND the page tabs.
export const FUNC_GROUPS = [
    { id: "listen", name: "Listen & Measure" },
    { id: "support", name: "Support & Resolve" },
    { id: "reach", name: "Reach Out" },
    { id: "operate", name: "Operate" },
];

// Legacy industry list — retained for backwards compatibility with a few
// existing spots (industry tags in copy). Not used to drive nav anymore.
export const INDUSTRIES = [
    "Industry-Agnostic",
    "Aviation",
    "Airports",
    "BFSI",
    "Telecom",
    "Healthcare",
    "Logistics",
];
export const industryKey = (i) => (i === 0 ? "agnostic" : INDUSTRIES[i]);

// ---------- USE CASES ----------
// Each: { id, name, func, one, p, s }
// `func` MUST match one of FUNC_GROUPS ids.
export const USE_CASES = [
    // ================ LISTEN & MEASURE (4) ================
    {
        id: "csat",
        func: "listen",
        name: "CSAT",
        one: "Automated post-call satisfaction, any language.",
        p: "Traditional surveys are slow, sampled and English-only. Managers see feedback days after the moment that matters, and human callback teams typically reach fewer than 5% of the customer base.",
        s: "A KyroLabs agent reaches out right after the interaction in a natural, conversational way (not a rigid survey form). It captures two scores, the customer's stated score AND a sentiment-analysis score, along with the reason in the customer's own language, and streams both into the QA dashboard. Because the agent scales, it reaches close to 100% of customers, not the <5% a human team can cover.",
    },
    {
        id: "voc",
        func: "listen",
        name: "Voice of Customer",
        one: "Every conversation coded, themes, sentiment, intent.",
        p: "Voice of Customer programs sample a fraction of calls and rely on manual coding weeks later.",
        s: "The agent listens to 100% of interactions, tags themes and sentiment in real time, and surfaces emerging issues before they escalate.",
    },
    {
        id: "fraud",
        func: "listen",
        name: "Fraud Detection",
        one: "Fraud detection on every conversation.",
        p: "The fraud team sees signals only after the loss; voice channels are still a blind spot.",
        s: "The agent scores every conversation against known fraud playbooks, catches voice-print and behaviour anomalies live, and routes cases to the investigation queue.",
    },
    {
        id: "risk",
        func: "listen",
        name: "Risk Analytics",
        one: "Capture portfolio-wide risk signals from customer interactions.",
        p: "Risk models miss the qualitative signal buried in conversations, hardship sentiment, complaint velocity.",
        s: "The agent extracts risk-relevant signals from every contact and feeds them into the risk analytics stack for earlier warning.",
    },

    // ================ SUPPORT & RESOLVE (13) ================
    {
        id: "faq",
        func: "support",
        name: "FAQ / Query",
        one: "Instant, grounded answers to common questions.",
        p: "First-line queues drown in repeat questions that already have documented answers.",
        s: "The agent handles the entire FAQ tier, grounded in your knowledge base, cited, and safe to say 'I don't know'.",
    },
    {
        id: "it-support",
        func: "support",
        name: "IT Support",
        one: "Tier-1 IT triage that resolves or routes.",
        p: "IT service desks are expensive, mostly triage, and slow on off-hours.",
        s: "The agent triages incidents, walks users through known fixes, opens tickets in ServiceNow / Jira, and routes properly.",
    },
    {
        id: "dealer",
        func: "support",
        name: "Dealer Support",
        one: "24/7 support for dealers, partners and channel.",
        p: "Channel partners can't reach the manufacturer's specialists at scale, every question becomes a bottleneck.",
        s: "The agent serves dealers with product specs, warranty rules, order status and lead qualification, 24/7 in local languages.",
    },
    {
        id: "field-service",
        func: "support",
        name: "Field Service",
        one: "AI companion for field technicians on the job.",
        p: "Technicians rely on tribal knowledge and phone calls when parts, procedures or diagnostics get complex on-site.",
        s: "The agent gives the technician instant answers, procedure recall, parts lookup and expert handoff on a mobile call, grounded in your service manuals.",
    },
    {
        id: "care",
        func: "support",
        name: "Customer Care",
        one: "General-purpose care across voice, chat and messaging.",
        p: "Care organisations need to handle a long tail of requests across every channel, headcount can't stretch that far.",
        s: "The agent is the always-on care layer: identifies the customer, understands intent, resolves what it can, hands off warmly when it can't.",
    },
    {
        id: "pax-experience",
        func: "support",
        name: "Passenger Experience",
        one: "Assistance across booking, day-of-travel and delays.",
        p: "Passenger experience calls are stateful and multi-source, booking, PNR, ops, loyalty, and hard to get right in one turn.",
        s: "The agent stitches PNR, ops and loyalty together into one voice- or chat-native conversation for every stage of travel.",
    },
    {
        id: "guest-experience",
        func: "support",
        name: "Guest Experience",
        one: "Concierge-grade help across the stay.",
        p: "Guests want fast, personal answers, housekeeping, F&B, local recommendations, that front desks can't scale.",
        s: "The agent is a 24/7 concierge on the guest's channel of choice, tied into PMS, F&B and local recommendations.",
    },
    {
        id: "patient-care",
        func: "support",
        name: "Patient Care",
        one: "Compliant patient assistance across the care journey.",
        p: "Patients navigate scheduling, refills, coverage and follow-up across disconnected systems.",
        s: "The agent handles compliant patient-facing tasks, scheduling, prep, refills, follow-up, inside HIPAA/DPDP-grade guardrails.",
    },
    {
        id: "digital-banking",
        func: "support",
        name: "Digital Banking",
        one: "Servicing for accounts, cards, payments and disputes.",
        p: "Digital banking servicing spans dozens of intents; IVRs and static bots frustrate customers.",
        s: "The agent handles account, card, payment and dispute intents on voice and chat with authentication, policy and audit built in.",
    },
    {
        id: "booking",
        func: "support",
        name: "Booking",
        one: "Book, change, refund, in one conversation.",
        p: "Booking, rebooking and refunds are stateful, policy-heavy, and split across web, app and call centre.",
        s: "The agent completes the whole booking journey inside a single conversation with PNR / PMS / OMS access and policy grounding.",
    },
    {
        id: "telemedicine",
        func: "support",
        name: "Telemedicine",
        one: "Triage, scheduling and intake for virtual care.",
        p: "Telemedicine platforms lose patients in the intake queue and need triage before a clinician engages.",
        s: "The agent performs symptom triage, verifies coverage, schedules a virtual visit and preps the clinician with structured intake.",
    },
    {
        id: "wealth-mgmt",
        func: "support",
        name: "Wealth Management",
        one: "Client servicing at scale for RM offices.",
        p: "Relationship managers spend too much time on servicing and too little on advising.",
        s: "The agent runs first-touch client servicing, statements, allocations, tax docs, meeting requests, and warms handoff to the RM only where advice is needed.",
    },
    {
        id: "after-sales",
        func: "support",
        name: "After-Sales",
        one: "Post-purchase support across warranty, service and parts.",
        p: "After-sales is a maze of warranty rules, parts availability, service slots and dealer coordination.",
        s: "The agent owns the end-to-end after-sales conversation with warranty lookup, service slot booking and dealer routing.",
    },

    // ================ REACH OUT (6) ================
    {
        id: "outbound",
        func: "reach",
        name: "AI Outbound Calling",
        one: "Proactive outbound calls at scale, without headcount.",
        p: "Outbound programs, feedback, verification, upsell, hit a hard headcount wall.",
        s: "The agent runs the entire outbound program on voice, in local languages, with campaign management, retries and analytics.",
    },
    {
        id: "sms",
        func: "reach",
        name: "SMS Keyword Callback",
        one: "Text a keyword, the agent calls back instantly.",
        p: "SMS keyword campaigns get a huge open rate but conversion breaks when nobody follows up.",
        s: "The agent picks up the keyword event, calls the customer back in seconds and closes the loop in the same conversation.",
    },
    {
        id: "renewals",
        func: "reach",
        name: "Renewals & Feedback",
        one: "Proactive renewals and structured feedback.",
        p: "Renewal windows lapse and feedback loops trail off, high value, low priority.",
        s: "The agent runs a proactive renewal outreach, captures structured feedback and drops the qualified ones to a human closer.",
    },
    {
        id: "warranty-amc",
        func: "reach",
        name: "Warranty & AMC",
        one: "Warranty and AMC renewals, on time, in language.",
        p: "Warranty and AMC bases are large, split across dealers, and hard to renew at scale.",
        s: "The agent runs warranty and AMC renewals across the base, on voice, in local languages, with dealer routing when needed.",
    },
    {
        id: "churn",
        func: "reach",
        name: "Churn",
        one: "Early-warning outreach for at-risk customers.",
        p: "Churn models fire signals; nobody acts on them at scale.",
        s: "The agent takes churn scores from your model, calls at-risk customers with tailored offers, and closes the save loop.",
    },
    {
        id: "personalization",
        func: "reach",
        name: "Personalization",
        one: "Personalised outreach on the moments that matter.",
        p: "Segmented campaigns still feel generic, messages don't match the customer's context or channel.",
        s: "The agent orchestrates 1:1 outreach across voice, WhatsApp, SMS and email based on live behaviour and campaign context.",
    },

    // ================ OPERATE (15) ================
    {
        id: "supply",
        func: "operate",
        name: "Supply Chain",
        one: "Supplier comms, exceptions and status at scale.",
        p: "Supply chain teams spend the day on the phone and on email chasing exceptions and status.",
        s: "The agent owns the routine supplier and internal comms, status pings, exception escalations, ETA updates, grounded in your TMS / ERP.",
    },
    {
        id: "flight-ops",
        func: "operate",
        name: "Flight Ops",
        one: "Ops-side comms during irregular operations.",
        p: "During IROPS, ops teams and crew comms swamp the channels, everything moves at the speed of a phone call.",
        s: "The agent handles crew and station comms during IROPS with structured messaging, callbacks and confirmations.",
    },
    {
        id: "cargo",
        func: "operate",
        name: "Cargo",
        one: "Airway bill status, exceptions and rate quoting.",
        p: "Cargo customers live on the phone: bookings, AWB status, rates, claims.",
        s: "The agent handles cargo booking, AWB tracking, exception updates and rate quoting on voice and messaging.",
    },
    {
        id: "fleet",
        func: "operate",
        name: "Fleet",
        one: "Driver and vehicle ops on voice and messaging.",
        p: "Fleet coordinators can't keep pace with the volume of driver, vehicle and dispatch conversations.",
        s: "The agent handles driver check-ins, ETA updates, exception routing and dispatcher hand-offs across the fleet.",
    },
    {
        id: "route-opt",
        func: "operate",
        name: "Route Optimization",
        one: "Live route decisions grounded in ops data.",
        p: "Route optimisation lives in one system while the humans making calls live in another.",
        s: "The agent surfaces live re-route recommendations to dispatchers and drivers, and closes the confirmation loop.",
    },
    {
        id: "maint-predict",
        func: "operate",
        name: "Maintenance Prediction",
        one: "Predictive maintenance, closed loop to the technician.",
        p: "Predictive maintenance signals fire but rarely translate into a scheduled intervention.",
        s: "The agent takes predictive signals, schedules the intervention, coordinates parts and dispatches the field technician.",
    },
    {
        id: "safety",
        func: "operate",
        name: "Safety",
        one: "Real-time safety and incident intake.",
        p: "Safety incident reporting is inconsistent, delayed and hard to standardise.",
        s: "The agent takes safety reports on voice or messaging, classifies them, and routes them into the safety management system.",
    },
    {
        id: "operations",
        func: "operate",
        name: "Operations",
        one: "General-purpose ops-desk automation.",
        p: "Ops desks glue the enterprise together with phone calls, spreadsheets and pings.",
        s: "The agent is the always-on ops desk companion, status checks, escalations, standard runbooks, grounded in your systems.",
    },
    {
        id: "smart-rooms",
        func: "operate",
        name: "Smart Rooms",
        one: "In-room voice control tied to hotel ops.",
        p: "In-room requests bounce between front desk, housekeeping and F&B with poor tracking.",
        s: "The agent handles in-room requests directly, tied into PMS, housekeeping and F&B systems, with SLA tracking.",
    },
    {
        id: "warehouse",
        func: "operate",
        name: "Warehouse",
        one: "Warehouse-floor voice ops.",
        p: "Warehouse teams need hands-free, voice-first tooling, screens don't survive the floor.",
        s: "The agent runs voice-first workflows for putaway, picking, exceptions and cycle counts on the warehouse floor.",
    },
    {
        id: "last-mile",
        func: "operate",
        name: "Last-Mile",
        one: "Delivery attempts, exceptions and customer comms.",
        p: "Last-mile is where NPS is made or lost, and where nobody has time to call every customer.",
        s: "The agent runs delivery-day comms, handles reschedules, coordinates with drivers and closes the exception loop.",
    },
    {
        id: "iot-tracking",
        func: "operate",
        name: "IoT Tracking",
        one: "Conversational access to live IoT tracking data.",
        p: "IoT data sits in dashboards; ops teams still ask on the phone 'where is it?'",
        s: "The agent answers live tracking questions on voice and messaging, grounded in the IoT platform.",
    },
    {
        id: "network-opt",
        func: "operate",
        name: "Network Optimization",
        one: "Field and NOC comms for network events.",
        p: "Network events span NOC, field crews and customer comms, coordination is manual.",
        s: "The agent coordinates NOC, field-crew and customer-facing communications during network events end to end.",
    },
    {
        id: "hospital-ops",
        func: "operate",
        name: "Hospital Ops",
        one: "Bed, OT and pharmacy coordination on voice.",
        p: "Hospital ops runs on paging and phone calls, bed status, OT scheduling, pharmacy.",
        s: "The agent runs bed status, OT scheduling and pharmacy coordination as conversational workflows.",
    },
    {
        id: "pharma-logistics",
        func: "operate",
        name: "Pharma Logistics",
        one: "Cold-chain and regulated pharma logistics comms.",
        p: "Pharma logistics is cold-chain sensitive and regulated, every exception is high stakes.",
        s: "The agent handles cold-chain comms, exception escalation and regulatory paperwork prompts across the shipment.",
    },
];

/** Group by function id. */
export function casesForFunc(funcId) {
    return USE_CASES.filter((u) => u.func === funcId);
}

/** Find by id. */
export function findUseCase(id) {
    return USE_CASES.find((u) => u.id === id);
}

/** Group-level meta used across the site. */
export const FUNC_META = {
    listen: {
        name: "Listen & Measure",
        short: "Hear every customer, score, code and analyse.",
        image: "/assets/images/img_uc_listen.webp",
        accent: "#8fd0ff",
    },
    support: {
        name: "Support & Resolve",
        short: "Answer, diagnose and resolve, any channel.",
        image: "/assets/images/img_uc_support.webp",
        accent: "#a78bfa",
    },
    reach: {
        name: "Reach Out",
        short: "Proactive calls, messages and follow-ups.",
        image: "/assets/images/img_uc_reach.webp",
        accent: "#34d399",
    },
    operate: {
        name: "Operate",
        short: "Run the ops layer, comms, exceptions, partners.",
        image: "/assets/images/img_uc_operate.webp",
        accent: "#fbbf24",
    },
};
